import { AfterViewInit, Directive, ElementRef, Input, NgZone, OnChanges, OnDestroy, Renderer2, SimpleChanges } from "@angular/core";

// Simple Implementation of ngx-ellipsis for Angular 6.00 MIT
// Once we finally update to Angular 9.00 we should use the library ngx-ellipsis
// Truncates the Text to fit without overflowing the element
// [truncateText]="lorem ippsum"
// Optional truncateTextEllipsis="..." to set Ellipsis
// Move to Core or another Module once tested, unless we are using ngx ellipsis

// ACHTUNG: es ist ein langsames system, welches die CPU belastet und damit die Anwendung verlangsamt. Es muss eine Performate Alternative gefunden werden.
// bzw. kann am SOT auch ein cache genutzt werden, weil die Größen der elemente bekannt sind.

const INNER_ELEMENT_CLASS = "truncatedText-inner";

@Directive({
  // tslint:disable-next-line: directive-selector
  selector: "[truncatedText]",
  exportAs: "truncatedText"
})
export class SOTTruncatedTextDirective implements OnDestroy, OnChanges, AfterViewInit {
  @Input("truncatedText") originalText: string;
  @Input() truncateTextEllipsis: string = "...";

  private innerElem: any;

  private resizeObserver: any; // ResizeObserver

  private previousDimensions: {
    width: number,
    height: number,
  };

  public constructor(
    private elementRef: ElementRef<HTMLElement>,
    private renderer: Renderer2,
    private ngZone: NgZone,
  ) { }

  ngAfterViewInit() {
    const elem = this.elementRef.nativeElement;
    this.previousDimensions = {
      width: elem.clientWidth,
      height: elem.clientHeight
    };
    this.applyTruncation();
  }

  ngOnChanges(changes: SimpleChanges) {
    const elem = this.elementRef.nativeElement;

    if (elem && this.originalText != null && this.truncateTextEllipsis != null && changes.originalText && !changes.originalText.firstChange && changes.originalText.currentValue !== changes.originalText.previousValue) {
      this.applyTruncation();
    }
  }

  ngOnDestroy(): void {
    this.removeResizeListener();
  }

  private createInnerElement() {
    const elem = this.elementRef.nativeElement;
    this.innerElem = this.renderer.createElement("div");
    this.renderer.addClass(this.innerElem, INNER_ELEMENT_CLASS);
    this.renderer.setStyle(this.innerElem, '-webkit-box-orient', 'vertical');
    const text = this.renderer.createText(this.originalText);
    this.renderer.appendChild(this.innerElem, text);
    this.renderer.appendChild(elem, this.innerElem);
  }

  /**
   * Calculate and apply the best Truncation possible
   */
  public applyTruncation() {
    if (!this.innerElem) {
      this.createInnerElement();
    }

    // Remove the resize listener as changing the contained text would trigger events:
    this.removeResizeListener();

    // Find the best length like bubblesort
    let best = 0;
    let mid: number;
    let rest = this.originalText.length;

    if (this.isOverflowing) {
      while (rest > 1) {
        mid = Math.floor(rest / 2);
        rest -= mid;
        this.truncateText(best + mid);
        if (!this.isOverflowing) {
          best += mid;
        }
      }
    } else {
      best = rest;
    }

    // Apply the best length:
    if (best > 0) {
      this.truncateText(best);
    } else { // no Space for text => render empty string without ellipsis
      this.renderer.setProperty(this.innerElem, "textContent", "");
    }

    // Re-attach the resize listener:
    this.addElementResizeListener();
  }

  private removeResizeListener() {
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }
  }

  /**
   * Set the truncated text to be displayed in the inner div
   * @param max the maximum length the text may have
   * @returns length of remaining text (excluding the ellipsisCharacters, if they were added)
   */
  private truncateText(max: number): number {
    let text = this.getTruncatedText(max);
    const truncatedLength = text.length;
    const isTextTruncated = (truncatedLength !== this.originalText.length);

    if (isTextTruncated) {
      text += this.truncateTextEllipsis;
    }
    this.renderer.setProperty(this.innerElem, "textContent", text);
    return truncatedLength;
  }

  /**
   * Get the original text's truncated version. If the text really needed to
   * be truncated, this.ellipsisCharacters will be appended.
   * @param max the maximum length the text may have
   * @return string the truncated string
   */
  private getTruncatedText(max: number): string {
    if (!this.originalText || this.originalText.length <= max) {
      return this.originalText;
    }
    // Implement Splitting here if needed
    return this.originalText.substr(0, max);
  }

  /**
   * Whether the text is exceeding the element's boundaries or not
   */
  private get isOverflowing(): boolean {
    const elem = this.elementRef.nativeElement;
    const currentOverflow = elem.style.overflow;
    if (!currentOverflow || currentOverflow === "visible") {
      elem.style.overflow = "hidden";
    }
    const isOverflowing = elem.clientWidth < elem.scrollWidth - 1 || elem.clientHeight < elem.scrollHeight - 1;
    // Reset overflow to the original configuration:
    elem.style.overflow = currentOverflow;
    return isOverflowing;
  }

  /**
   * Set up an event listener to call applyEllipsis() whenever the element
   * has been resized.
   */
  private addElementResizeListener() {
    const elem = this.elementRef.nativeElement;
    this.removeResizeListener();
    this.resizeObserver = new (window as any).ResizeObserver(() => {
      window.requestAnimationFrame(() => {
        if (this.previousDimensions.width !== elem.clientWidth || this.previousDimensions.height !== elem.clientHeight) {
          this.ngZone.run(() => {
            this.applyTruncation();
          });

          this.previousDimensions.width = elem.clientWidth;
          this.previousDimensions.height = elem.clientHeight;
        }
      });
    });
    this.resizeObserver.observe(elem);
  }
}
